import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { ClipboardCheck, Search, Loader2, Truck, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FulfilledOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchOrders = async () => {
    try { setOrders((await axios.get(`${API}/fulfilled-orders`)).data); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { fetchOrders(); }, []);

  const handleDeliver = async (order) => {
    if (!window.confirm(`Marcar ${order.code} como entregue e descontar ${order.current_good_quantity} peças do stock?`)) return;
    try {
      await axios.put(`${API}/fulfilled-orders/${order.id}/deliver`, { quantity: order.current_good_quantity });
      toast.success(`${order.code} entregue! Stock atualizado.`);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleExportPdf = (o) => {
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const qualityRate = (o.current_good_quantity + o.current_rejected_quantity) > 0
      ? ((o.current_good_quantity / (o.current_good_quantity + o.current_rejected_quantity)) * 100).toFixed(1) : "100";
    const html = `<!DOCTYPE html><html><head><title>${o.code}</title>
    <style>
      @page{size:A4;margin:15mm} body{font-family:Arial,sans-serif;color:#1e293b;font-size:11px}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:20px}
      .header img{height:50px} h1{font-size:18px;color:#0f172a}
      table{width:100%;border-collapse:collapse;margin:15px 0} th{background:#0f172a;color:#f59e0b;padding:8px 12px;text-align:left}
      td{padding:6px 12px;border-bottom:1px solid #e5e7eb} .footer{margin-top:30px;border-top:1px solid #e5e7eb;padding-top:10px;color:#9ca3af;font-size:9px;text-align:center}
    </style></head><body>
    <div class="header"><img src="${logoUrl}" alt="EP3D" /><div style="text-align:right;font-size:10px"><strong>${o.code}</strong><br>Encomenda Finalizada</div></div>
    <h1>Relatório de Encomenda — ${o.code}</h1>
    <table>
      <tr><th>Campo</th><th>Valor</th></tr>
      <tr><td>Código</td><td><strong>${o.code}</strong></td></tr>
      <tr><td>Peça</td><td>${o.part_code} — ${o.part_description}</td></tr>
      <tr><td>Máquina</td><td>${o.machine_code}</td></tr>
      <tr><td>Estado</td><td>${o.status === "delivered" ? "ENTREGUE" : "CONCLUÍDA"}</td></tr>
      <tr><td>Quantidade Planeada</td><td>${(o.good_quantity_target || 0).toLocaleString()}</td></tr>
      <tr><td>Quantidade Produzida</td><td><strong>${(o.current_good_quantity || 0).toLocaleString()}</strong></td></tr>
      <tr><td>Rejeitada</td><td>${(o.current_rejected_quantity || 0).toLocaleString()}</td></tr>
      <tr><td>Taxa Qualidade</td><td>${qualityRate}%</td></tr>
      <tr><td>MP Consumida</td><td>${o.raw_material_consumed || 0} kg</td></tr>
      <tr><td>Data Criação</td><td>${o.created_at ? new Date(o.created_at).toLocaleString("pt-PT") : "-"}</td></tr>
      <tr><td>Data Início</td><td>${o.started_at ? new Date(o.started_at).toLocaleString("pt-PT") : "-"}</td></tr>
      <tr><td>Data Conclusão</td><td>${o.completed_at ? new Date(o.completed_at).toLocaleString("pt-PT") : "-"}</td></tr>
      ${o.notes ? `<tr><td>Notas</td><td>${o.notes}</td></tr>` : ""}
    </table>
    <div class="footer"><p>EP3D Produção — ${o.code} — Gerado em ${new Date().toLocaleString("pt-PT")}</p></div>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `Encomenda_${o.code}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const filtered = orders.filter(o =>
    (o.code || "").toLowerCase().includes(search.toLowerCase()) ||
    (o.part_code || "").toLowerCase().includes(search.toLowerCase()) ||
    (o.part_description || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalProduced = orders.reduce((s, o) => s + (o.current_good_quantity || 0), 0);
  const totalDelivered = orders.filter(o => o.status === "delivered").length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="fulfilled-orders-page">
      <div>
        <h1 className="font-chivo text-3xl font-bold text-foreground flex items-center gap-3"><ClipboardCheck className="h-7 w-7" /> Encomendas Finalizadas</h1>
        <p className="text-muted-foreground mt-1">Ordens de produção concluídas e entregas</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-muted-foreground">Total Finalizadas</p><p className="text-3xl font-bold">{orders.length}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-green-600">Total Produzido</p><p className="text-3xl font-bold text-green-600">{totalProduced.toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="pt-4 text-center"><p className="text-xs text-blue-600">Entregues</p><p className="text-3xl font-bold text-blue-600">{totalDelivered}</p></CardContent></Card>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Encomendas</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Peça</TableHead>
              <TableHead>Máquina</TableHead>
              <TableHead className="text-right">Planeado</TableHead>
              <TableHead className="text-right">Produzido</TableHead>
              <TableHead className="text-right">Rejeitado</TableHead>
              <TableHead>Data Conclusão</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Sem encomendas finalizadas</TableCell></TableRow>
              ) : filtered.map(o => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono font-bold">{o.code}</TableCell>
                  <TableCell>{o.part_code} <span className="text-xs text-muted-foreground">{o.part_description}</span></TableCell>
                  <TableCell>{o.machine_code}</TableCell>
                  <TableCell className="text-right font-mono">{(o.good_quantity_target || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-green-600">{(o.current_good_quantity || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{(o.current_rejected_quantity || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-sm">{o.completed_at ? new Date(o.completed_at).toLocaleDateString("pt-PT") : "-"}</TableCell>
                  <TableCell>
                    {o.status === "delivered" ? <Badge className="bg-blue-500 text-white">Entregue</Badge> : <Badge className="bg-green-500 text-white">Concluída</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-500" onClick={() => handleExportPdf(o)} title="Exportar PDF"><FileText className="h-4 w-4" /></Button>
                      {o.status !== "delivered" && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-green-500" onClick={() => handleDeliver(o)} title="Marcar Entregue"><Truck className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
